import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { LoggingService } from '../../../core/services/logging.service';
import { AuthService } from '../../../core/services/auth.service';

/**
 * HTTP error interceptor for API call failures
 *
 * Captures HTTP errors (4xx, 5xx) and logs them to backend with full context.
 * Logs include request URL, method, status code, and error response.
 *
 * Handles 401 errors by clearing auth state and redirecting to login.
 *
 * Errors are re-thrown to preserve normal error handling flow.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const loggingService = inject(LoggingService);
  const router = inject(Router);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only log HTTP errors (not client-side errors like network failures)
      // Check for client-side errors: ErrorEvent, ProgressEvent, or status 0
      const isClientError =
        error.error instanceof ErrorEvent ||
        error.error instanceof ProgressEvent ||
        (error.status === 0 &&
          !(error.error instanceof Object && 'message' in error.error));

      if (isClientError) {
        // Client-side or network error
        const errorMessage =
          error.error instanceof ErrorEvent
            ? error.error.message
            : 'Network error occurred';
        const clientError = new Error(errorMessage);
        loggingService.error(
          'Client-side error during HTTP request',
          clientError,
          {
            component: 'HttpErrorInterceptor',
            url: req.url,
            method: req.method,
            errorType: 'ClientError',
          }
        );
      } else {
        // Backend returned unsuccessful response code
        loggingService.error(
          `HTTP error: ${error.status} ${error.statusText}`,
          undefined,
          {
            component: 'HttpErrorInterceptor',
            url: req.url,
            method: req.method,
            statusCode: error.status,
            statusText: error.statusText,
            errorResponse: safeStringify(error.error),
            errorType: 'HttpError',
          }
        );

        // Handle session expiration
        if (error.status === 401) {
          // Clear local auth state
          authService.setCurrentUser(null);

          // Redirect to login ONLY if:
          // 1. NOT already on login/auth pages
          // 2. NOT calling BFF auth endpoints (status, login, logout)
          const currentUrl = router.url;
          const isAuthEndpoint = req.url.includes('/api/auth/');
          const isOnAuthPage =
            currentUrl.includes('/login') || currentUrl.includes('/auth/');

          if (!isAuthEndpoint && !isOnAuthPage) {
            router.navigate(['/login']);
          }
        }
      }

      // Re-throw error to preserve normal error handling
      return throwError(() => error);
    })
  );
};

/**
 * Safely stringify error response (handle circular references)
 */
function safeStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
}
